import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import EditFacultyDialog from './EditFacultyDialog';
import AddFacultyDialog from './AddFacultyDialog';
import './Faculties.css';

const Faculties = () => {
  const navigate = useNavigate();
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [draggedFaculty, setDraggedFaculty] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [dragOverFaculty, setDragOverFaculty] = useState(null);

  useEffect(() => {
    fetchFaculties();
  }, []);

  const fetchFaculties = async () => {
    try {
      setLoading(true);
      const response = await api.get('/faculties');
      if (response.data.success) {
        // Sort by index
        const sortedFaculties = response.data.data.sort((a, b) => {
          if (a.index !== undefined && b.index !== undefined) {
            return a.index - b.index;
          }
          return 0;
        });
        setFaculties(sortedFaculties);
      } else {
        setError('Failed to fetch faculties');
      }
    } catch (err) {
      setError('Error loading faculties. Please try again.');
      console.error('Error fetching faculties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (faculty) => {
    setEditingFaculty(faculty);
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditingFaculty(null);
  };

  const handleEditSave = (updatedFaculty) => {
    setFaculties(prevFaculties => 
      prevFaculties.map(faculty => 
        faculty._id === updatedFaculty._id ? updatedFaculty : faculty
      )
    );
  };

  const handleEditDelete = (facultyId) => {
    setFaculties(prevFaculties => 
      prevFaculties.filter(faculty => faculty._id !== facultyId)
    );
  };

  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setIsAddDialogOpen(false);
  };

  const handleAddSave = (newFaculty) => {
    setFaculties(prevFaculties => [newFaculty, ...prevFaculties]);
  };

  const handleViewDetails = (faculty) => {
    navigate(`/faculties/${faculty._id}`, { 
      state: { faculty: faculty } 
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, faculty) => {
    setDraggedFaculty(faculty);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, faculty) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedFaculty && draggedFaculty._id !== faculty._id) {
      setDragOverFaculty(faculty._id);
    }
  };

  const handleDrop = (e, targetFaculty) => {
    e.preventDefault();
    
    if (!draggedFaculty || draggedFaculty._id === targetFaculty._id) {
      return;
    }

    const draggedIndex = faculties.findIndex(f => f._id === draggedFaculty._id);
    const targetIndex = faculties.findIndex(f => f._id === targetFaculty._id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new array with reordered faculties
    const newFaculties = [...faculties];
    const [removed] = newFaculties.splice(draggedIndex, 1);
    newFaculties.splice(targetIndex, 0, removed);

    // Update indices based on new order
    const updatedFaculties = newFaculties.map((faculty, index) => ({
      ...faculty,
      index: index
    }));

    setFaculties(updatedFaculties);
    setDraggedFaculty(null);
    setDragOverFaculty(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedFaculty(null);
    setDragOverFaculty(null);
  };

  // Send updated indices to backend
  const sendUpdatedIndices = React.useCallback(async () => {
    if (!isReordering) {
      return;
    }

    try {
      const indices = faculties.map(faculty => ({
        _id: faculty._id,
        index: faculty.index
      }));

      const response = await api.put('/faculties/reorder', {
        faculties: indices
      });

      if (response.data.success) {
        setIsReordering(false);
      } else {
        // Revert to original order if update fails
        fetchFaculties();
      }
    } catch (err) {
      console.error('Error updating faculty order:', err);
      // Revert to original order if update fails
      fetchFaculties();
    } finally {
      setIsReordering(false);
    }
  }, [faculties, isReordering]);

  // Auto-save indices after reordering
  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedIndices();
      }, 1000); // Wait 1 second after last reorder

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isReordering, sendUpdatedIndices]);

  if (loading) {
    return (
      <div className="faculties-loading">
        <div className="loading-spinner"></div>
        <p>Loading faculties...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="faculties-error">
        <p>{error}</p>
        <button onClick={fetchFaculties} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="faculties-section">
      <div className="section-header">
        <div className="header-content">
          <div className="header-text">
            <h2>Faculties</h2>
            <p>Manage and view all faculties in the system</p>
          </div>
          <button className="add-faculty-button" onClick={handleAddClick}>
            <span className="button-icon">+</span>
            Add New Faculty
          </button>
        </div>
      </div>
      
      <div className="faculties-grid">
        {faculties.map((faculty) => (
          <div
            key={faculty._id}
            className={`faculty-card ${draggedFaculty?._id === faculty._id ? 'dragging' : ''} ${dragOverFaculty === faculty._id ? 'drag-over' : ''}`}
            draggable={true}
            onDragStart={(e) => handleDragStart(e, faculty)}
            onDragOver={(e) => handleDragOver(e, faculty)}
            onDrop={(e) => handleDrop(e, faculty)}
            onDragEnd={handleDragEnd}
          >
            <div className="faculty-header">
              {faculty.coverImage && (
                <img
                  src={faculty.coverImage}
                  alt=""
                  className="faculty-cover-thumb"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="faculty-content">
                <h3 className="faculty-name">{faculty.name}</h3>
                <p className="faculty-name-en">{faculty.nameEn}</p>
              </div>
              <div className="drag-icon-container">
                <span className="drag-icon">⋮⋮</span>
              </div>
            </div>
            <div className="faculty-actions">
              <button 
                className="edit-button"
                onClick={() => handleEditClick(faculty)}
              >
                Edit
              </button>
              <button 
                className="view-button"
                onClick={() => handleViewDetails(faculty)}
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {faculties.length === 0 && (
        <div className="no-faculties">
          <p>No faculties found. Add your first faculty to get started.</p>
        </div>
      )}

      <EditFacultyDialog
        faculty={editingFaculty}
        isOpen={isEditDialogOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        onDelete={handleEditDelete}
      />

      <AddFacultyDialog
        isOpen={isAddDialogOpen}
        onClose={handleAddClose}
        onSave={handleAddSave}
      />
    </div>
  );
};

export default Faculties;
